import { CircleHelp, SlidersHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup';
import { RangeSlider } from '@/components/ui/RangeSlider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useGetRecommendationSetting } from '@/hooks/useGetRecommendationSetting';
import { useUpdateRecommendationSetting } from '@/hooks/useUpdateRecommendationSetting';
import { priceFormatterByLocale } from '@/lib/utils';
import {
  DiscountFilterType,
  Filters,
  RecommendationSettingType,
} from '@/services/RecommendationSettingsService';

const PRICE_RANGE_MAX_DEFAULT = 10000;
const PRICE_RANGE_ABOVE_MIN = 10000;
const PRICE_RANGE_API_MAX = 1000000;
const DISCOUNT_RANGE_MIN = 0;
const DISCOUNT_RANGE_MAX = 100;

type PriceFilterMode = 'SLIDER' | 'ABOVE_10K' | 'ALL_PRODUCTS';

const recommendationSettingTypes = [
  RecommendationSettingType.PRODUCT,
  RecommendationSettingType.CATEGORY,
  RecommendationSettingType.SHOP_THE_LOOK,
  RecommendationSettingType.CUSTOM_SHELF,
] as const;

function toSafeNumber(value: unknown, fallback: number) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return fallback;
  }

  return number;
}

function createInitialFilters(): Filters {
  return {
    brokenStock: {
      active: false,
    },
    discount: {
      type: DiscountFilterType.ALL,
      range: {
        from: DISCOUNT_RANGE_MIN,
        to: DISCOUNT_RANGE_MAX,
      },
      active: false,
    },
    price: {
      range: {
        from: 0,
        to: PRICE_RANGE_MAX_DEFAULT,
      },
      active: false,
    },
    lowStock: {
      stockLimit: 10,
      active: false,
    },
  };
}

function createFiltersByTypeInitialState() {
  return {
    [RecommendationSettingType.PRODUCT]: createInitialFilters(),
    [RecommendationSettingType.CATEGORY]: createInitialFilters(),
    [RecommendationSettingType.SHOP_THE_LOOK]: createInitialFilters(),
    [RecommendationSettingType.CUSTOM_SHELF]: createInitialFilters(),
  } as Record<RecommendationSettingType, Filters>;
}

function createDirtyByTypeInitialState() {
  return {
    [RecommendationSettingType.PRODUCT]: false,
    [RecommendationSettingType.CATEGORY]: false,
    [RecommendationSettingType.SHOP_THE_LOOK]: false,
    [RecommendationSettingType.CUSTOM_SHELF]: false,
  } as Record<RecommendationSettingType, boolean>;
}

function createPriceModeByTypeInitialState() {
  return {
    [RecommendationSettingType.PRODUCT]: 'SLIDER',
    [RecommendationSettingType.CATEGORY]: 'SLIDER',
    [RecommendationSettingType.SHOP_THE_LOOK]: 'SLIDER',
    [RecommendationSettingType.CUSTOM_SHELF]: 'SLIDER',
  } as Record<RecommendationSettingType, PriceFilterMode>;
}

function resolvePriceFilterMode(filters?: Filters | null): PriceFilterMode {
  const priceFrom = toSafeNumber(filters?.price?.range?.from, 0);
  const priceTo = toSafeNumber(
    filters?.price?.range?.to,
    PRICE_RANGE_MAX_DEFAULT,
  );

  if (priceTo >= PRICE_RANGE_API_MAX && priceFrom <= 0) {
    return 'ALL_PRODUCTS';
  }

  if (priceTo >= PRICE_RANGE_API_MAX && priceFrom >= PRICE_RANGE_ABOVE_MIN) {
    return 'ABOVE_10K';
  }

  return 'SLIDER';
}

function normalizeDiscountType(value: unknown) {
  if (value === DiscountFilterType.WITH_DISCOUNT) {
    return DiscountFilterType.WITH_DISCOUNT;
  }

  if (value === DiscountFilterType.WITHOUT_DISCOUNT) {
    return DiscountFilterType.WITHOUT_DISCOUNT;
  }

  return DiscountFilterType.ALL;
}

function normalizeFilters(filters?: Filters | null): Filters {
  if (!filters) {
    return createInitialFilters();
  }

  const discountType = normalizeDiscountType(filters.discount?.type);
  const discountFrom = Math.max(
    DISCOUNT_RANGE_MIN,
    Math.min(
      toSafeNumber(filters.discount?.range?.from, DISCOUNT_RANGE_MIN),
      DISCOUNT_RANGE_MAX,
    ),
  );
  const discountTo = Math.max(
    discountFrom,
    Math.min(
      toSafeNumber(filters.discount?.range?.to, DISCOUNT_RANGE_MAX),
      DISCOUNT_RANGE_MAX,
    ),
  );
  const priceFromRaw = Math.min(
    PRICE_RANGE_MAX_DEFAULT,
    Math.max(0, toSafeNumber(filters.price?.range?.from, 0)),
  );
  const priceToRaw = Math.max(
    priceFromRaw,
    Math.min(
      PRICE_RANGE_MAX_DEFAULT,
      toSafeNumber(filters.price?.range?.to, PRICE_RANGE_MAX_DEFAULT),
    ),
  );
  const priceFrom = Math.min(priceFromRaw, priceToRaw);
  const priceTo = Math.max(priceFromRaw, priceToRaw);

  return {
    brokenStock: {
      active: Boolean(filters.brokenStock?.active),
    },
    discount: {
      type: discountType,
      range: {
        from: discountFrom,
        to: discountTo,
      },
      active:
        discountType !== DiscountFilterType.ALL &&
        Boolean(filters.discount?.active),
    },
    price: {
      range: {
        from: priceFrom,
        to: priceTo,
      },
      active: Boolean(filters.price?.active),
    },
    lowStock: {
      stockLimit: Math.max(0, toSafeNumber(filters.lowStock?.stockLimit, 10)),
      active: Boolean(filters.lowStock?.active),
    },
  };
}

function isFiltersEmpty(filters: Filters) {
  return (
    !filters.brokenStock.active &&
    !filters.discount.active &&
    !filters.price.active &&
    !filters.lowStock.active
  );
}

export function RecommendationSettingsModal() {
  const { user } = useAuth();
  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.Personalization.components.RecommendationImplementationGuide.settingsModal',
  });
  const { t: tProductFilters } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Slice.components.SliceProducts.components.ProductFilters',
  });

  const [isOpen, setIsOpen] = useState(false);
  const [activeType, setActiveType] = useState<RecommendationSettingType>(
    RecommendationSettingType.PRODUCT,
  );
  const [filtersByType, setFiltersByType] = useState<
    Record<RecommendationSettingType, Filters>
  >(createFiltersByTypeInitialState);
  const [dirtyByType, setDirtyByType] = useState<
    Record<RecommendationSettingType, boolean>
  >(createDirtyByTypeInitialState);
  const [priceModeByType, setPriceModeByType] = useState<
    Record<RecommendationSettingType, PriceFilterMode>
  >(createPriceModeByTypeInitialState);

  const productSettingQuery = useGetRecommendationSetting(
    RecommendationSettingType.PRODUCT,
    isOpen,
  );
  const categorySettingQuery = useGetRecommendationSetting(
    RecommendationSettingType.CATEGORY,
    isOpen,
  );
  const shopTheLookSettingQuery = useGetRecommendationSetting(
    RecommendationSettingType.SHOP_THE_LOOK,
    isOpen,
  );
  const customShelfSettingQuery = useGetRecommendationSetting(
    RecommendationSettingType.CUSTOM_SHELF,
    isOpen,
  );
  const { updateRecommendationSetting, isLoading: isSaving } =
    useUpdateRecommendationSetting();

  useEffect(() => {
    if (
      !isOpen ||
      !productSettingQuery.recommendationSetting ||
      dirtyByType.PRODUCT
    ) {
      return;
    }

    setPriceModeByType((previous) => ({
      ...previous,
      PRODUCT: resolvePriceFilterMode(
        productSettingQuery.recommendationSetting?.filters,
      ),
    }));

    setFiltersByType((previous) => ({
      ...previous,
      PRODUCT: normalizeFilters(
        productSettingQuery.recommendationSetting?.filters,
      ),
    }));
  }, [isOpen, dirtyByType.PRODUCT, productSettingQuery.recommendationSetting]);

  useEffect(() => {
    if (
      !isOpen ||
      !categorySettingQuery.recommendationSetting ||
      dirtyByType.CATEGORY
    ) {
      return;
    }

    setPriceModeByType((previous) => ({
      ...previous,
      CATEGORY: resolvePriceFilterMode(
        categorySettingQuery.recommendationSetting?.filters,
      ),
    }));

    setFiltersByType((previous) => ({
      ...previous,
      CATEGORY: normalizeFilters(
        categorySettingQuery.recommendationSetting?.filters,
      ),
    }));
  }, [
    isOpen,
    categorySettingQuery.recommendationSetting,
    dirtyByType.CATEGORY,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !shopTheLookSettingQuery.recommendationSetting ||
      dirtyByType.SHOP_THE_LOOK
    ) {
      return;
    }

    setPriceModeByType((previous) => ({
      ...previous,
      SHOP_THE_LOOK: resolvePriceFilterMode(
        shopTheLookSettingQuery.recommendationSetting?.filters,
      ),
    }));

    setFiltersByType((previous) => ({
      ...previous,
      SHOP_THE_LOOK: normalizeFilters(
        shopTheLookSettingQuery.recommendationSetting?.filters,
      ),
    }));
  }, [
    isOpen,
    dirtyByType.SHOP_THE_LOOK,
    shopTheLookSettingQuery.recommendationSetting,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !customShelfSettingQuery.recommendationSetting ||
      dirtyByType.CUSTOM_SHELF
    ) {
      return;
    }

    setPriceModeByType((previous) => ({
      ...previous,
      CUSTOM_SHELF: resolvePriceFilterMode(
        customShelfSettingQuery.recommendationSetting?.filters,
      ),
    }));

    setFiltersByType((previous) => ({
      ...previous,
      CUSTOM_SHELF: normalizeFilters(
        customShelfSettingQuery.recommendationSetting?.filters,
      ),
    }));
  }, [
    isOpen,
    dirtyByType.CUSTOM_SHELF,
    customShelfSettingQuery.recommendationSetting,
  ]);

  const queriesByType = {
    [RecommendationSettingType.PRODUCT]: productSettingQuery,
    [RecommendationSettingType.CATEGORY]: categorySettingQuery,
    [RecommendationSettingType.SHOP_THE_LOOK]: shopTheLookSettingQuery,
    [RecommendationSettingType.CUSTOM_SHELF]: customShelfSettingQuery,
  };

  const currentFilters = filtersByType[activeType];
  const currentPriceMode = priceModeByType[activeType];
  const currentQuery = queriesByType[activeType];
  const shouldShowLoadMessage =
    currentQuery.isError ||
    (!currentQuery.isLoading && !currentQuery.recommendationSetting);
  const isDisabled = isSaving || currentQuery.isLoading;

  function handleOpenChange(open: boolean) {
    setIsOpen(open);

    if (!open) {
      setActiveType(RecommendationSettingType.PRODUCT);
      setFiltersByType(createFiltersByTypeInitialState());
      setDirtyByType(createDirtyByTypeInitialState());
      setPriceModeByType(createPriceModeByTypeInitialState());
    }
  }

  function updateCurrentFilters(updater: (filters: Filters) => Filters) {
    setFiltersByType((previous) => ({
      ...previous,
      [activeType]: updater(previous[activeType]),
    }));

    setDirtyByType((previous) => ({
      ...previous,
      [activeType]: true,
    }));
  }

  function handleReset() {
    setFiltersByType((previous) => ({
      ...previous,
      [activeType]: createInitialFilters(),
    }));
    setPriceModeByType((previous) => ({
      ...previous,
      [activeType]: 'SLIDER',
    }));

    setDirtyByType((previous) => ({
      ...previous,
      [activeType]: true,
    }));
  }

  async function handleSave() {
    const priceRange =
      currentPriceMode === 'ALL_PRODUCTS'
        ? { from: 0, to: PRICE_RANGE_API_MAX }
        : currentPriceMode === 'ABOVE_10K'
          ? { from: PRICE_RANGE_ABOVE_MIN, to: PRICE_RANGE_API_MAX }
          : {
              from: currentFilters.price.range.from,
              to: currentFilters.price.range.to,
            };

    const filtersToSave = isFiltersEmpty(currentFilters)
      ? null
      : {
          ...currentFilters,
          price: {
            ...currentFilters.price,
            active:
              currentPriceMode === 'SLIDER'
                ? currentFilters.price.active
                : true,
            range: priceRange,
          },
        };

    try {
      await updateRecommendationSetting({
        type: activeType,
        dto: {
          filters: filtersToSave,
        },
      });

      setDirtyByType((previous) => ({
        ...previous,
        [activeType]: false,
      }));

      toast.success(
        t('toast.success', {
          type: t(`types.${activeType}`),
        }),
      );
    } catch {
      toast.error(t('toast.error'));
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" className="gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          {t('openButton')}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeType}
          onValueChange={(value) =>
            setActiveType(value as RecommendationSettingType)
          }
        >
          <TabsList className="grid h-auto w-full grid-cols-1 gap-2 bg-transparent p-0 sm:grid-cols-4">
            {recommendationSettingTypes.map((type) => (
              <TabsTrigger
                key={type}
                value={type}
                className="h-auto whitespace-normal text-center"
              >
                {t(`types.${type}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {shouldShowLoadMessage && (
          <p className="text-sm text-red-500">{t('loadError')}</p>
        )}

        <div className="grid gap-6 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Label htmlFor="recommendation-broken-stock">
                {tProductFilters('filters.fullSizeRun')}
              </Label>
              <Popover>
                <PopoverTrigger>
                  <CircleHelp className="h-4 w-4 cursor-pointer text-muted-foreground" />
                </PopoverTrigger>
                <PopoverContent className="w-80 p-2">
                  <p className="text-sm text-muted-foreground">
                    {tProductFilters('filters.fullSizeRunDescription')}
                  </p>
                </PopoverContent>
              </Popover>
            </div>

            <Switch
              id="recommendation-broken-stock"
              disabled={isDisabled}
              checked={currentFilters.brokenStock.active}
              onCheckedChange={(checked) =>
                updateCurrentFilters((filters) => ({
                  ...filters,
                  brokenStock: {
                    active: checked,
                  },
                }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="recommendation-discount-filter">
              {tProductFilters('filters.discount')}
            </Label>

            <Select
              disabled={isDisabled}
              value={currentFilters.discount.type}
              onValueChange={(value: DiscountFilterType) =>
                updateCurrentFilters((filters) => ({
                  ...filters,
                  discount: {
                    ...filters.discount,
                    type: value,
                    active: value !== DiscountFilterType.ALL,
                  },
                }))
              }
            >
              <SelectTrigger id="recommendation-discount-filter">
                <SelectValue
                  placeholder={tProductFilters('filters.discount')}
                />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value={DiscountFilterType.ALL}>
                  {tProductFilters('filters.allProducts')}
                </SelectItem>
                <SelectItem value={DiscountFilterType.WITH_DISCOUNT}>
                  {tProductFilters('filters.productsWithDiscount')}
                </SelectItem>
                <SelectItem value={DiscountFilterType.WITHOUT_DISCOUNT}>
                  {tProductFilters('filters.productsWithoutDiscount')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {currentFilters.discount.type ===
            DiscountFilterType.WITH_DISCOUNT && (
            <div className="space-y-4">
              <Label htmlFor="recommendation-discount-range">
                {tProductFilters('filters.discountRange')}
              </Label>

              <RangeSlider
                id="recommendation-discount-range"
                disabled={isDisabled}
                className="w-full"
                min={DISCOUNT_RANGE_MIN}
                max={DISCOUNT_RANGE_MAX}
                step={1}
                value={[
                  currentFilters.discount.range.from,
                  currentFilters.discount.range.to,
                ]}
                onValueChange={(value) =>
                  updateCurrentFilters((filters) => ({
                    ...filters,
                    discount: {
                      type: DiscountFilterType.WITH_DISCOUNT,
                      range: {
                        from: value[0],
                        to: value[1],
                      },
                      active: true,
                    },
                  }))
                }
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{currentFilters.discount.range.from}%</span>
                <span>{currentFilters.discount.range.to}%</span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <Label htmlFor="recommendation-price-range">
              {tProductFilters('filters.priceRange')}
            </Label>

            <RangeSlider
              id="recommendation-price-range"
              disabled={isDisabled || currentPriceMode !== 'SLIDER'}
              min={0}
              max={PRICE_RANGE_MAX_DEFAULT}
              value={[
                currentFilters.price.range.from,
                currentFilters.price.range.to,
              ]}
              onValueChange={(value) => {
                updateCurrentFilters((filters) => ({
                  ...filters,
                  price: {
                    range: {
                      from: value[0],
                      to: value[1],
                    },
                    active: true,
                  },
                }));
              }}
              className="w-full"
            />

            <div className="space-y-2">
              <Label>{t('priceModeLabel')}</Label>
              <RadioGroup
                value={currentPriceMode}
                onValueChange={(value) => {
                  const nextMode = value as PriceFilterMode;

                  setPriceModeByType((previous) => ({
                    ...previous,
                    [activeType]: nextMode,
                  }));

                  setDirtyByType((previous) => ({
                    ...previous,
                    [activeType]: true,
                  }));

                  if (nextMode !== 'SLIDER') {
                    updateCurrentFilters((filters) => ({
                      ...filters,
                      price: {
                        ...filters.price,
                        active: true,
                      },
                    }));
                  }
                }}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    id="recommendation-price-mode-slider"
                    value="SLIDER"
                    disabled={isDisabled}
                  />
                  <Label
                    htmlFor="recommendation-price-mode-slider"
                    className="font-normal"
                  >
                    {t('priceMode.slider')}
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    id="recommendation-price-mode-above-10k"
                    value="ABOVE_10K"
                    disabled={isDisabled}
                  />
                  <Label
                    htmlFor="recommendation-price-mode-above-10k"
                    className="font-normal"
                  >
                    {t('priceMode.above10k')}
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    id="recommendation-price-mode-all-products"
                    value="ALL_PRODUCTS"
                    disabled={isDisabled}
                  />
                  <Label
                    htmlFor="recommendation-price-mode-all-products"
                    className="font-normal"
                  >
                    {t('priceMode.allProducts')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex justify-between text-sm text-muted-foreground">
              <span>
                {currentPriceMode === 'ALL_PRODUCTS'
                  ? priceFormatterByLocale(
                      0,
                      user?.customer.language || 'pt-BR',
                    )
                  : currentPriceMode === 'ABOVE_10K'
                    ? priceFormatterByLocale(
                        PRICE_RANGE_ABOVE_MIN,
                        user?.customer.language || 'pt-BR',
                      )
                    : priceFormatterByLocale(
                        currentFilters.price.range.from,
                        user?.customer.language || 'pt-BR',
                      )}
              </span>
              <span>
                {currentPriceMode === 'SLIDER'
                  ? priceFormatterByLocale(
                      currentFilters.price.range.to,
                      user?.customer.language || 'pt-BR',
                    )
                  : priceFormatterByLocale(
                      PRICE_RANGE_API_MAX,
                      user?.customer.language || 'pt-BR',
                    )}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="recommendation-low-stock">
                {tProductFilters('filters.lowStock')}
              </Label>

              <Switch
                id="recommendation-low-stock"
                disabled={isDisabled}
                checked={currentFilters.lowStock.active}
                onCheckedChange={(checked) =>
                  updateCurrentFilters((filters) => ({
                    ...filters,
                    lowStock: {
                      ...filters.lowStock,
                      active: checked,
                    },
                  }))
                }
              />
            </div>

            {currentFilters.lowStock.active && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Label htmlFor="recommendation-low-stock-limit">
                    {tProductFilters('filters.lowStockLimit')}
                  </Label>

                  <Popover>
                    <PopoverTrigger>
                      <CircleHelp className="h-4 w-4 cursor-pointer text-muted-foreground" />
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-2">
                      <p className="text-sm text-muted-foreground">
                        {tProductFilters('filters.lowStockDescription')}
                      </p>
                    </PopoverContent>
                  </Popover>
                </div>

                <Input
                  id="recommendation-low-stock-limit"
                  type="number"
                  disabled={isDisabled}
                  className="w-full"
                  value={currentFilters.lowStock.stockLimit}
                  onChange={(event) => {
                    const stockLimit = Math.max(
                      0,
                      toSafeNumber(event.target.value, 0),
                    );

                    updateCurrentFilters((filters) => ({
                      ...filters,
                      lowStock: {
                        stockLimit,
                        active: true,
                      },
                    }));
                  }}
                />
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              className="w-full"
              disabled={isDisabled}
              onClick={handleSave}
            >
              {t('saveButton')}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={isDisabled}
              onClick={handleReset}
            >
              {tProductFilters('filters.clearButtonLabel')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
